// Followup Styles - Centralized styling for all Followup components

export const colors = {
  primary: '#7c3aed',
  primaryLight: '#F3E8FF',
  primaryHover: '#6d28d9',

  danger: '#EF4444',
  dangerLight: '#FEE2E2',
  dangerBg: '#ffe5e5',

  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  warningText: '#D97706',

  success: '#10B981',
  successLight: '#D1FAE5',

  info: '#3B82F6',
  infoLight: '#DBEAFE',

  gray: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },

  white: '#FFFFFF',
  border: '#E5E7EB',
};

export const animations = {
  fadeIn: {
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  },
  slideUp: {
    '@keyframes slideUp': {
      from: { opacity: 0, transform: 'translateY(20px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  },
  slideIn: {
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateX(-20px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
  },
  scaleIn: {
    '@keyframes scaleIn': {
      from: { opacity: 0, transform: 'scale(0.95)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
  },
};

// Header Styles
export const headerStyles = {
  title: {
    fontWeight: 700,
    color: colors.gray[800],
    fontSize: '24px',
  },

  pendingBadge: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'primary.main',
    backgroundColor: (theme) => `${theme.palette.primary.main}1A`,
    px: 2,
    py: 0.6,
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    letterSpacing: '0.4px',
    lineHeight: 1,
  },

  dateButton: {
    borderRadius: '10px',
    textTransform: 'none',
    color: colors.gray[600],
    borderColor: colors.gray[100],
    fontSize: '14px',
    fontWeight: 500,
    px: 2,
    '&:hover': {
      borderColor: colors.gray[100],
      bgcolor: colors.gray[100],
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.2s ease',
  },

  priorityButton: {
    borderRadius: '10px',
    textTransform: 'none',
    color: colors.gray[600],
    borderColor: colors.border,
    fontSize: '14px',
    fontWeight: 500,
    px: 2,
    '&:hover': {
      borderColor: colors.gray[100],
      bgcolor: colors.gray[100],
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.2s ease',
  },

  newTaskButton: {
    borderRadius: '10px',
    textTransform: 'none',
    bgcolor: 'primary.main',
    fontSize: '14px',
    fontWeight: 600,
    px: 3,
    py: 1,
    boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}4D`,
    '&:hover': {
      bgcolor: 'primary.dark',
      boxShadow: (theme) => `0 6px 16px ${theme.palette.primary.main}66`,
      transform: 'translateY(-2px)',
    },
    '&:active': {
      bgcolor: 'primary.dark',
      transform: 'translateY(0)',
    },
    transition: 'all 0.2s ease',
  },

  statCard: {
    flex: '1 1 200px',
    minWidth: 200,
    maxWidth: 400,
    p: 2.5,
    borderRadius: '12px',
    bgcolor: colors.white,
    border: '1px solid',
    borderColor: colors.gray[200],
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    },
  },

  statIconBox: {
    width: 48,
    height: 48,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  statLabel: {
    fontSize: '12px',
    fontWeight: 700,
    color: colors.gray[400],
    letterSpacing: '0.5px',
    mb: 0.5,
  },

  statValue: {
    fontWeight: 700,
    color: colors.gray[800],
    fontSize: '24px',
  },
};

// Table Styles
export const tableStyles = {
  container: {
    bgcolor: colors.white,
    minHeight: '90vh',
    p: 0,
  },

  tableWrapper: {
    bgcolor: colors.white,
    borderRadius: 2,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },

  headerRow: {
    display: 'grid',
    gridTemplateColumns: '100px 90px 180px 130px 180px 160px 140px 200px 140px',
    alignItems: 'center',
    px: 3,
    py: 2,
    bgcolor: '#FAFAFA',
    borderBottom: '1px solid #F0F0F0',
  },

  headerText: {
    fontSize: 11,
    fontWeight: 600,
    color: colors.gray[400],
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },

  dataRow: (isHovered) => ({
    display: 'grid',
    gridTemplateColumns: '100px 90px 180px 130px 180px 160px 140px 200px 140px',
    alignItems: 'center',
    px: 3,
    py: 2.5,
    bgcolor: isHovered ? '#FAFBFC' : colors.white,
    borderBottom: '1px solid #F5F5F5',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  }),

  contactButton: {
    width: 30,
    height: 30,
    bgcolor: colors.gray[100],
    border: 'none',
    borderRadius: '6px',
    transition: 'all 0.2s',
    '&:hover': {
      bgcolor: colors.gray[100],
      transform: 'translateY(-2px)',
    },
  },

  viewDetailsButton: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
    color: 'primary.main',
    bgcolor: 'transparent',
    borderColor: 'primary.main',
    borderRadius: '6px',
    px: 2,
    py: 0.5,
    minWidth: 'auto',
    transition: 'all 0.2s',
    '&:hover': {
      bgcolor: 'primary.main',
      color: colors.white,
      borderColor: 'primary.main',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      bgcolor: 'primary.dark',
      borderColor: 'primary.dark',
    },
  },

  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: 3,
    px: 1,
  },

  paginationText: {
    fontSize: 12,
    color: colors.gray[400],
    fontWeight: 500,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
};

// Modal Styles
export const modalStyles = {
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  paper: {
    bgcolor: colors.white,
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    outline: 'none',
    overflow: 'hidden',
    animation: 'scaleIn 0.3s ease-out',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 3,
    borderBottom: `1px solid ${colors.gray[200]}`,
  },

  title: {
    fontWeight: 700,
    fontSize: '20px',
    color: colors.gray[900],
  },

  subtitle: {
    fontSize: '14px',
    color: colors.gray[500],
    mt: 0.5,
  },

  closeButton: {
    color: colors.gray[400],
    '&:hover': {
      bgcolor: colors.gray[100],
      color: colors.gray[600],
    },
  },

  content: {
    p: 3,
  },

  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
    p: 3,
    borderTop: `1px solid ${colors.gray[200]}`,
    bgcolor: colors.gray[50],
  },

  cancelButton: {
    textTransform: 'none',
    color: colors.gray[600],
    fontWeight: 500,
    px: 3,
    '&:hover': {
      bgcolor: colors.gray[100],
    },
  },

  submitButton: {
    textTransform: 'none',
    bgcolor: colors.primary,
    fontWeight: 600,
    px: 4,
    py: 1.25,
    borderRadius: '8px',
    boxShadow: `0 4px 12px rgba(124,58,237,0.3)`,
    '&:hover': {
      bgcolor: colors.primaryHover,
      boxShadow: `0 6px 16px rgba(124,58,237,0.4)`,
    },
    transition: 'all 0.2s ease',
  },

  sectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[500],
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    mb: 1.5,
  },

  inputLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: colors.gray[700],
    mb: 1,
  },

  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '14px',
      '& fieldset': {
        borderColor: colors.gray[200],
      },
      '&:hover fieldset': {
        borderColor: colors.gray[300],
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary,
      },
    },
  },

  selectField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '14px',
      '& fieldset': {
        borderColor: colors.gray[200],
      },
    },
  },
};

// Calendar Styles
export const calendarStyles = {
  container: {
    border: `1px solid ${colors.gray[200]}`,
    borderRadius: '12px',
    p: 2,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 2,
  },

  monthYear: {
    fontWeight: 600,
    fontSize: '15px',
    color: colors.gray[800],
  },

  navButton: {
    width: 28,
    height: 28,
    color: colors.gray[400],
    '&:hover': {
      bgcolor: colors.gray[100],
      color: colors.gray[600],
    },
  },

  weekDays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 0.5,
    mb: 1,
  },

  weekDay: {
    fontSize: '11px',
    fontWeight: 600,
    color: colors.gray[400],
    textAlign: 'center',
    py: 1,
  },

  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 0.5,
    userSelect: 'none',
  },

  day: (isSelected, isToday, isDisabled, isInRange = false, isRangeStart = false, isRangeEnd = false) => ({
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: (isRangeStart || isRangeEnd) ? 600 : isSelected ? 600 : 400,
    color: isDisabled
      ? colors.gray[300]
      : (isRangeStart || isRangeEnd)
        ? colors.white
        : isInRange
          ? colors.primary
          : isSelected
            ? colors.white
            : colors.gray[700],
    bgcolor: (isRangeStart || isRangeEnd)
      ? 'primary.main'
      : isInRange
        ? colors.primaryLight
        : isSelected
          ? 'primary.main'
          : isToday
            ? colors.primaryLight
            : 'transparent',
    cursor: isDisabled ? 'default' : 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      bgcolor: isDisabled
        ? 'transparent'
        : (isRangeStart || isRangeEnd)
          ? 'primary.dark'
          : isInRange
            ? colors.primaryLight
            : isSelected
              ? 'primary.main'
              : colors.gray[100],
    },
  }),
};

// Priority Toggle Styles
export const priorityToggleStyles = {
  container: {
    display: 'flex',
    border: `1px solid ${colors.gray[200]}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },

  option: (isSelected, type) => {
    const bgColors = {
      low: isSelected ? colors.gray[100] : 'transparent',
      medium: isSelected ? colors.warningLight : 'transparent',
      high: isSelected ? colors.dangerLight : 'transparent',
    };

    const textColors = {
      low: isSelected ? colors.gray[700] : colors.gray[400],
      medium: isSelected ? colors.warningText : colors.gray[400],
      high: isSelected ? colors.danger : colors.gray[400],
    };

    return {
      flex: 1,
      py: 1,
      px: 2,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: textColors[type],
      bgcolor: bgColors[type],
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:not(:last-child)': {
        borderRight: `1px solid ${colors.gray[200]}`,
      },
      '&:hover': {
        bgcolor: isSelected ? bgColors[type] : colors.gray[50],
      },
    };
  },
};

// Priority Menu Styles
export const priorityMenuStyles = {
  paper: {
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
    border: `1px solid ${colors.gray[200]}`,
    minWidth: 200,
    mt: 1,
  },

  menuItem: (isSelected) => ({
    py: 1.5,
    px: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: colors.gray[700],
    bgcolor: isSelected ? colors.gray[50] : 'transparent',
    '&:hover': {
      bgcolor: colors.gray[100],
    },
  }),

  checkmark: {
    fontSize: 18,
    color: colors.primary,
  },

  priorityDot: (priority) => {
    const dotColors = {
      high: colors.danger,
      medium: colors.warning,
      low: colors.gray[400],
    };

    return {
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: dotColors[priority] || 'transparent',
      mr: 1.5,
    };
  },
};

// Date Picker Styles
export const datePickerStyles = {
  paper: {
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
    border: `1px solid ${colors.gray[200]}`,
    minWidth: 300,
    p: 2,
  },

  quickOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    mb: 2,
    pb: 2,
    borderBottom: `1px solid ${colors.gray[200]}`,
  },

  quickOption: (isSelected) => ({
    py: 1,
    px: 2,
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: isSelected ? 500 : 400,
    color: isSelected ? '#fff' : colors.gray[700],
    bgcolor: isSelected ? 'primary.main' : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: isSelected ? 'primary.main' : colors.gray[100],
    },
  }),
};